import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar3, Check2, Clock, ExclamationTriangle, Facebook, Link45deg, Person, Whatsapp } from "react-bootstrap-icons";
import PageHero from "../../components/public/PageHero";
import Seo, { ORIGIN } from "../../components/public/Seo";
import { CONTACT, ASSETS } from "../../components/landing/data/landingData";
import { BlogImage } from "../../components/landing/BlogSection";
import { getBlogBySlug, listBlogs, sanitizeArticleHtml } from "./blogApi";

/** /blogs/:slug - article page (public API GET /all/blogs/{id}; found by slug through the public list). */
const BlogDetailPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const hintId = location.state?.id;

  const [state, setState] = useState({ status: "loading", blog: null });
  const [related, setRelated] = useState([]);
  const [copied, setCopied] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading", blog: null });
    setRelated([]);
    window.scrollTo({ top: 0 });

    getBlogBySlug(slug, hintId)
      .then((blog) => {
        if (cancelled) return;
        setState({ status: "success", blog });
        return listBlogs({ page: 1, size: 4 }).then((res) => {
          if (!cancelled) setRelated(res.blogs.filter((b) => b.id !== blog.id).slice(0, 3));
        });
      })
      .catch((error) => {
        if (!cancelled) setState({ status: error?.message === "not-found" ? "notfound" : "error", blog: null });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, hintId, attempt]);

  const { blog } = state;
  const safeHtml = useMemo(() => sanitizeArticleHtml(blog?.html), [blog]);
  const path = `/blogs/${slug}`;
  const url = `${ORIGIN}${path}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy this link", url);
    }
  };

  if (state.status === "loading") {
    return (
      <>
        <PageHero title="Loading article..." crumbs={[{ label: "Blog", to: "/blogs" }, { label: "Article" }]} />
        <section className="lp-section" aria-busy="true">
          <div className="container lp-article">
            <div className="lp-skeleton lp-article__hero mb-4" />
            <div className="lp-skeleton lp-skeleton--line lp-skeleton--lg w-75" />
            <div className="lp-skeleton lp-skeleton--line w-100" />
            <div className="lp-skeleton lp-skeleton--line w-100" />
            <div className="lp-skeleton lp-skeleton--line w-50" />
          </div>
        </section>
      </>
    );
  }

  if (state.status !== "success") {
    const missing = state.status === "notfound";
    return (
      <>
        <Seo title="Article not found | All Home Desire" path={path} />
        <PageHero title={missing ? "Article not found" : "Something went wrong"} crumbs={[{ label: "Blog", to: "/blogs" }, { label: "Article" }]} />
        <section className="lp-section">
          <div className="container">
            <div className="lp-empty" role="alert">
              <ExclamationTriangle aria-hidden="true" />
              <p>{missing ? "This article may have been moved or removed." : "We couldn't load this article right now."}</p>
              <div className="d-flex gap-2">
                {!missing && <button type="button" className="lp-btn lp-btn--outline-dark" onClick={() => setAttempt((n) => n + 1)}>Try again</button>}
                <Link to="/blogs" className="lp-btn lp-btn--primary">Back to Blog</Link>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  const description = blog.metaDescription || blog.excerpt;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: blog.title,
    description,
    image: blog.image ? [blog.image] : undefined,
    author: { "@type": "Person", name: blog.author },
    datePublished: blog.isoDate || undefined,
    mainEntityOfPage: url,
    publisher: { "@type": "Organization", name: "All Home Desire" },
  };

  return (
    <>
      <Seo
        title={`${blog.metaTitle || blog.title} | All Home Desire`}
        description={description}
        path={path}
        image={blog.image || undefined}
        type="article"
        jsonLd={jsonLd}
      />
      <PageHero title={blog.title} crumbs={[{ label: "Blog", to: "/blogs" }, { label: blog.title }]}>
        <p className="lp-article__meta mt-3" style={{ color: "rgba(255,255,255,0.85)" }}>
          <span><Person aria-hidden="true" /> {blog.author}</span>
          {blog.date && <span><Calendar3 aria-hidden="true" /> {blog.date}</span>}
          <span><Clock aria-hidden="true" /> {blog.readTime}</span>
        </p>
      </PageHero>

      <article className="lp-section">
        <div className="container">
          <div className="lp-article">
            <Link to="/blogs" className="lp-link mb-4 d-inline-flex"><ArrowLeft aria-hidden="true" /> Back to Blog</Link>
            {blog.image && (
              <img className="lp-article__hero mb-4" src={blog.image} alt={blog.title} width="1200" height="600"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = ASSETS.hero; }} />
            )}
            <div className="lp-prose" dangerouslySetInnerHTML={{ __html: safeHtml }} />

            <div className="lp-share mt-5 pt-4 border-top">
              <strong>Share</strong>
              <button type="button" className="lp-share__btn" onClick={copyLink}>
                {copied ? <Check2 aria-hidden="true" /> : <Link45deg aria-hidden="true" />} {copied ? "Link copied" : "Copy link"}
              </button>
              <a className="lp-share__btn" target="_blank" rel="noopener noreferrer" href={`https://wa.me/?text=${encodeURIComponent(`${blog.title} ${url}`)}`}>
                <Whatsapp aria-hidden="true" /> WhatsApp
              </a>
              <a className="lp-share__btn" target="_blank" rel="noopener noreferrer" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`}>
                <Facebook aria-hidden="true" /> Facebook
              </a>
            </div>

            <aside className="lp-card p-4 mt-5 text-center">
              <h2 className="lp-card__title">Need help with your property search?</h2>
              <p className="lp-muted">Talk to an All Home Desire property expert.</p>
              <div className="d-flex flex-wrap justify-content-center gap-2">
                <a href={CONTACT.phoneHref} className="lp-btn lp-btn--primary">Call {CONTACT.phone}</a>
                <Link to="/contactus" className="lp-btn lp-btn--outline-dark">Contact Us</Link>
              </div>
            </aside>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="lp-section lp-section--alt" aria-labelledby="related-title">
          <div className="container">
            <h2 id="related-title" className="lp-heading__title mb-4">Related articles</h2>
            <div className="row g-4">
              {related.map((b) => (
                <div key={b.key} className="col-12 col-md-6 col-lg-4">
                  <article className="lp-card lp-card--lift h-100">
                    <Link to={`/blogs/${b.slug}`} state={{ id: b.id }} className="lp-blog-featured__link" aria-label={`Read ${b.title}`}>
                      <BlogImage blog={b} />
                      <div className="p-4">
                        <h3 className="lp-blog__title lp-clamp-2">{b.title}</h3>
                        <span className="lp-link">Read Article <ArrowRight aria-hidden="true" /></span>
                      </div>
                    </Link>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default BlogDetailPage;
